import { BusinessLabelChips } from "./BusinessLabelChips";
import {
  findUnknownLabelNames,
  formatUnknownLabelsWarning,
  parseLabelNames,
  parsePicValues,
  PIC_DISPLAY_DELIMITER,
} from "constants/sideLoadedBusinesses";
import {
  Input,
  SelectField,
  SelectFieldOption,
  TextArea,
} from "components/Form/FormikControls";
import { useField, useFormikContext } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { getBusinessById } from "selectors";
import { selectBusinessLabelsAggridData } from "selectors/businessLabel";
import { TableCell, TableRow } from "@material-ui/core";
import styled from "styled-components/macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  getSideLoadedBusinessMappableOptionsById,
  getSideLoadedBusinessById,
} from "selectors/sideLoadedBusinesses";
import { faPencil } from "@fortawesome/pro-solid-svg-icons";
import {
  FieldConfiguration,
  FieldGroup,
  NOTES_FIELD_INDICES,
  PIC_FIELD_INDICES,
} from "./fieldConfigurations";
import { Row } from "components/Layout";
import {
  selectBusinessIdByAgentCodeLookup,
  selectBusinessIdBySaleyardCodeLookup,
} from "selectors/indexes";
import { Tooltip } from "components/Form/FormikControls/Tooltip";

import { Error } from "components/Form/FormikControls/Error";

import { useMountEffect } from "hooks";

export interface SideLoadedFieldRowProps {
  configuration: FieldConfiguration;
  businessId?: string;
  recordId: string;
}

const TableRowStatus = {
  ValidAndMapped: "ValidAndMapped",
  InvalidAndMapped: "InvalidAndMapped",
  WarnedAndMapped: "WarnedAndMapped",
  UnMapped: "UnMapped",
};

export const ActiveTableRow = styled(TableRow)`
  background-color: ${({ status, theme }) => {
    switch (status) {
      case TableRowStatus.ValidAndMapped:
        return theme.colors.lightGreen;
      case TableRowStatus.InvalidAndMapped:
        return theme.colors.error;
      case TableRowStatus.WarnedAndMapped:
        return theme.colors.warning;
      case TableRowStatus.UnMapped:
        return theme.colors.gray85;
    }
  }};
`;

interface MappableFieldSelectorProps {
  name: string;
  recordId: string;
  formatOption?: (option: SelectFieldOption) => React.ReactNode;
}

const OptionHeading = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.alpha}px;
  color: ${({ theme }) => theme.colors.titleLight};
  text-transform: uppercase;
`;

function formatOptionLabel(option: SelectFieldOption): React.ReactNode {
  return (
    <div>
      <OptionHeading>{option.value}</OptionHeading>
      <div>{option.label}</div>
    </div>
  );
}

function formatPicsOptionLabel(option: SelectFieldOption): React.ReactNode {
  return (
    <div>
      <OptionHeading>{option.value}</OptionHeading>
      <div>{parsePicValues([option.label]).join(PIC_DISPLAY_DELIMITER)}</div>
    </div>
  );
}

function formatLabelsOptionLabel(option: SelectFieldOption): React.ReactNode {
  return (
    <div>
      <OptionHeading>{option.value}</OptionHeading>
      <BusinessLabelChips names={parseLabelNames(option.label)} />
    </div>
  );
}

function MappableFieldSelector({
  name,
  recordId,
  formatOption = formatOptionLabel,
}: MappableFieldSelectorProps) {
  const options = useSelector(
    getSideLoadedBusinessMappableOptionsById(recordId),
  );
  return (
    <SelectField
      name={name}
      options={options}
      placeholder="Not mapped..."
      isClearable
      formatOptionLabel={formatOption}
    />
  );
}

function BusinessLookup({ valueFieldName }) {
  const valueField = useField(valueFieldName)[0];

  const businessIdByAgentCodeLookup = useSelector(
    selectBusinessIdByAgentCodeLookup as (
      state: State,
    ) => Record<string, string>,
  );
  const businessIdBySaleyardCodeLookup = useSelector(
    selectBusinessIdBySaleyardCodeLookup as (
      state: State,
    ) => Record<string, string>,
  );

  // Try and pull it out of the lookup.
  const businessIdFromAgentCode = businessIdByAgentCodeLookup[valueField.value];
  const businessIdFromSaleyardCode =
    businessIdBySaleyardCodeLookup[valueField.value];

  const businessId = businessIdFromAgentCode || businessIdFromSaleyardCode;

  const mappedBusiness = useSelector(getBusinessById(businessId)) || null;

  if (!mappedBusiness) {
    return (
      <div>
        No business found for {valueField.value}.{" "}
        <Tooltip title="This value needs to map to the Agent Code or Saleyard Code of an existing business - does a business with that code exist?" />
      </div>
    );
  }

  return (
    <div>
      {mappedBusiness.name}{" "}
      <Tooltip
        title={`Found business from ${businessIdFromAgentCode ? "Agent Code" : "Saleyard Code"}`}
      />
    </div>
  );
}

function NotesValueEditor({
  configuration,
  recordId,
}: {
  configuration: FieldConfiguration;
  recordId: string;
}) {
  const mappableFieldName = `MappableField__${configuration.value}`;
  const valueFieldName = `Value__${configuration.value}`;

  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  const notesFieldKeys = useMemo(
    () =>
      NOTES_FIELD_INDICES.map(
        index => `MappableField__${configuration.value}__${index}`,
      ),
    [configuration.value],
  );

  const { values } = useFormikContext();

  // Snapshot of only the note dropdown values so we can depend on them without listing N variables
  const noteValuesSignature = JSON.stringify(
    notesFieldKeys.map(k => values[k]),
  );

  const [valueFieldField, , valueFieldHelpers] = useField(valueFieldName);
  const setValueFieldValue = valueFieldHelpers.setValue;

  const mappableFieldHelpers = useField(mappableFieldName)[2];
  const setMappableFieldValue = mappableFieldHelpers.setValue;

  // Only recompute when the note dropdown selections change, not on every form keystroke
  const defaultNotesValue = useMemo(() => {
    const noteValues = JSON.parse(noteValuesSignature) as (
      | string
      | undefined
    )[];
    return noteValues
      .filter(
        (fieldName: string) =>
          fieldName !== undefined &&
          sideLoadedBusiness.rawData[fieldName] !== undefined,
      )
      .map(
        fieldName => `${fieldName}: ${sideLoadedBusiness.rawData[fieldName]}`,
      )
      .join("\n");
  }, [sideLoadedBusiness, noteValuesSignature]);

  useEffect(() => {
    // If the default notes change, because a user changes the drop down, set the input value to the new default (this does override a users changes.)
    setValueFieldValue(defaultNotesValue);
    // And make sure the mappable field is set, too.
    setMappableFieldValue(true);
  }, [defaultNotesValue, setValueFieldValue, setMappableFieldValue]);

  useMountEffect(() => {
    // Set the value, if it's defined, on first load.
    if (defaultNotesValue) {
      setValueFieldValue(defaultNotesValue);
    }
  });

  return (
    <React.Fragment>
      {notesFieldKeys.map(fieldName => (
        <MappableFieldSelector
          name={fieldName}
          recordId={recordId}
          key={fieldName}
        />
      ))}
      <MappableFieldValueDisplay
        mappableFieldValue={valueFieldField.value}
        valueFieldName={valueFieldName}
        asTextArea
      />
    </React.Fragment>
  );
}

// Several source columns feed one collected value, as the notes row does.
function PicsValueEditor({
  configuration,
  recordId,
}: {
  configuration: FieldConfiguration;
  recordId: string;
}) {
  const mappableFieldName = `MappableField__${configuration.value}`;
  const valueFieldName = `Value__${configuration.value}`;

  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  const picFieldKeys = useMemo(
    () =>
      PIC_FIELD_INDICES.map(
        index => `MappableField__${configuration.value}__${index}`,
      ),
    [configuration.value],
  );

  const { values, validateForm } = useFormikContext();

  // Snapshot of only the PIC dropdowns, so form keystrokes don't recompute.
  const picValuesSignature = JSON.stringify(picFieldKeys.map(k => values[k]));

  const [valueFieldField, , valueFieldHelpers] = useField(valueFieldName);
  const setValueFieldValue = valueFieldHelpers.setValue;

  const [mappableField, , mappableFieldHelpers] = useField(mappableFieldName);
  const setMappableFieldValue = mappableFieldHelpers.setValue;

  const defaultPicsValue = useMemo(() => {
    const columnNames = JSON.parse(picValuesSignature) as (
      | string
      | undefined
    )[];
    return parsePicValues(
      columnNames.map(columnName =>
        columnName === undefined
          ? undefined
          : sideLoadedBusiness.rawData[columnName],
      ),
    ).join(PIC_DISPLAY_DELIMITER);
  }, [sideLoadedBusiness, picValuesSignature]);

  useEffect(() => {
    // Changing a dropdown overrides a hand edit.
    setValueFieldValue(defaultPicsValue, false);
    setMappableFieldValue(true, false);
  }, [defaultPicsValue, setValueFieldValue, setMappableFieldValue]);

  // Each setValue validates against the values from before the other, so
  // validate once both have rendered.
  useEffect(() => {
    validateForm();
  }, [validateForm, valueFieldField.value, mappableField.value]);

  return (
    <React.Fragment>
      {picFieldKeys.map(fieldName => (
        <MappableFieldSelector
          name={fieldName}
          recordId={recordId}
          key={fieldName}
          formatOption={formatPicsOptionLabel}
        />
      ))}
      <MappableFieldValueDisplay
        mappableFieldValue={valueFieldField.value}
        valueFieldName={valueFieldName}
      />
    </React.Fragment>
  );
}

interface MappableFieldValueDisplayProps {
  mappableFieldValue: string;
  valueFieldName: string;
  isBusinessLookup?: boolean;
  asTextArea?: boolean;
  warning?: string | null;
}
function MappableFieldValueDisplay({
  mappableFieldValue,
  valueFieldName,
  isBusinessLookup,
  asTextArea,
  warning,
}: MappableFieldValueDisplayProps) {
  const [isEditing, setIsEditing] = useState(false);

  const [valueField, valueMeta] = useField(valueFieldName);

  if (mappableFieldValue === null) {
    return null;
  }

  if (isBusinessLookup) {
    // BusinessLookup says which value it couldn't resolve, which is the same
    // failure the validation reports.
    return <BusinessLookup valueFieldName={valueFieldName} />;
  }

  if (isEditing) {
    // Input and TextArea render the error themselves.
    return (
      <>
        {asTextArea ? (
          <TextArea
            name={valueFieldName}
            rows="8"
            label=""
            errorColor="black"
          />
        ) : (
          <Input name={valueFieldName} disableAutoComplete errorColor="black" />
        )}
        {!valueMeta.error && warning && <Error color="black">{warning}</Error>}
      </>
    );
  }

  return (
    <>
      <span onClick={() => setIsEditing(true)} className="cursor-pointer">
        <FontAwesomeIcon icon={faPencil} />
        &nbsp;{asTextArea ? <pre>{valueField.value}</pre> : valueField.value}
      </span>
      {valueMeta.error && <Error color="black">{valueMeta.error}</Error>}
      {!valueMeta.error && warning && <Error color="black">{warning}</Error>}
    </>
  );
}

function SideLoadedFieldRowInner({
  configuration,
  businessId,
  recordId,
}: SideLoadedFieldRowProps) {
  const sideLoadedBusiness = useSelector(getSideLoadedBusinessById(recordId));

  const valueFieldName = `Value__${configuration.value}`;
  const mappableFieldName = `MappableField__${configuration.value}`;

  const valueFieldElements = useField(valueFieldName);
  const valueFieldValue = valueFieldElements[0].value;
  const valueMeta = valueFieldElements[1];
  const valueFieldHelpers = valueFieldElements[2];
  const mappableFieldValue = useField(mappableFieldName)[0].value;

  const setValueFieldValue = valueFieldHelpers.setValue;

  const businessLabels = useSelector(selectBusinessLabelsAggridData);

  const { handleAsNotes, handleAsProperty } = configuration;

  // These keep their mappable field switched on so the editor stays reachable,
  // so it's their content, not the flag, that says whether anything is mapped.
  const collectsManyColumns = handleAsNotes || handleAsProperty;
  const isMapped = collectsManyColumns
    ? Boolean(valueFieldValue)
    : mappableFieldValue !== null;

  // A label has to exist before it can be applied, and one that doesn't is
  // skipped on commit rather than blocking the record, so say so here instead
  // of failing validation.
  const labelsWarning = useMemo(() => {
    if (!configuration.handleAsLabels || !isMapped) {
      return null;
    }
    return formatUnknownLabelsWarning(
      findUnknownLabelNames(
        parseLabelNames(valueFieldValue),
        businessLabels.map(label => label.name),
      ),
    );
  }, [configuration.handleAsLabels, isMapped, valueFieldValue, businessLabels]);

  let rowStatus = TableRowStatus.UnMapped;
  if (isMapped) {
    if (valueMeta.error) {
      rowStatus = TableRowStatus.InvalidAndMapped;
    } else if (labelsWarning) {
      rowStatus = TableRowStatus.WarnedAndMapped;
    } else {
      rowStatus = TableRowStatus.ValidAndMapped;
    }
  }

  useEffect(() => {
    // Notes and PICs handle their own state/changes.
    if (!collectsManyColumns) {
      if (mappableFieldValue === null) {
        setValueFieldValue("");
      } else {
        setValueFieldValue(sideLoadedBusiness.rawData[mappableFieldValue]);
      }
    }
  }, [
    mappableFieldValue,
    sideLoadedBusiness,
    setValueFieldValue,
    collectsManyColumns,
  ]);

  const BusinessValueRenderer = configuration.businessValueRenderer || null;
  const existingValue = BusinessValueRenderer ? (
    <BusinessValueRenderer businessId={businessId} />
  ) : null;
  return (
    <ActiveTableRow status={rowStatus}>
      <TableCell>
        <strong>{configuration.label}</strong>
        <br />
        <i>{existingValue}</i>
      </TableCell>
      <TableCell>
        {handleAsNotes && (
          <NotesValueEditor configuration={configuration} recordId={recordId} />
        )}
        {handleAsProperty && (
          <PicsValueEditor configuration={configuration} recordId={recordId} />
        )}
        {!collectsManyColumns && (
          <React.Fragment>
            <MappableFieldSelector
              name={mappableFieldName}
              recordId={recordId}
              formatOption={
                configuration.handleAsLabels
                  ? formatLabelsOptionLabel
                  : formatOptionLabel
              }
            />
            <MappableFieldValueDisplay
              mappableFieldValue={mappableFieldValue}
              valueFieldName={valueFieldName}
              isBusinessLookup={configuration.isBusinessLookup}
              warning={labelsWarning}
            />
          </React.Fragment>
        )}
      </TableCell>
    </ActiveTableRow>
  );
}

export function SideLoadedFieldGroupHeadingRow({
  group,
}: {
  group: FieldGroup;
}) {
  return (
    <TableRow>
      <TableCell colSpan={2}>
        <Row marginLeft={4}>
          <h2>{group}</h2>
        </Row>
      </TableCell>
    </TableRow>
  );
}

export const SideLoadedFieldRow = React.memo(SideLoadedFieldRowInner);
